import { AUTHORIZATION_HEADER, SESSION_ID_HEADER } from '../api/api.constants';
import { SESSION_EXPIRED_FAULT_CODE } from '../api/api.fault.constants';
import { Application } from '../application/application';
import { allApplications } from '../application/application.helpers';
import {
  CapturedErrorApiMessage,
  CapturedErrorApiMessageConfigPath,
  CapturedErrorBase,
  DEFAULT_ERROR_SEVERITY,
  DEFAULT_ERROR_SUBTYPE,
  DEFAULT_ERROR_TYPE
} from './errorHandling.types';

// ─── Deduplication ──────────────────────────────────

/** Time-to-live for deduplication hashes in milliseconds. */
export const DEDUPLICATION_TTL_MS = 5000;

/** Maximum number of deduplication entries before cleanup. */
export const DEDUPLICATION_MAX_ENTRIES = 100;

// ─── Scope Identifiers ──────────────────────────────

/** Scope identifier for the unified order flow. Used by mutations to route debug logs to orderFlowDebug. */
export const ERROR_SCOPE_ORDER_FLOW = 'orderFlow';

// ─── Captured Error Constants ───────────────────────
// (consolidated from errorHandling.capturedError.constants)

/** Defaults for an empty "API message" portion of a captured error. */
export const CAPTURED_ERROR_API_MESSAGE_BASE: CapturedErrorApiMessage = {
  config: {},
  request: undefined,
  response: {
    data: undefined,
    status: 200,
    statusText: ''
  }
};

/** Locations on a captured error where the "API message" configuration exists. */
export const CAPTURED_ERROR_API_MESSAGE_CONFIG_PATHS: CapturedErrorApiMessageConfigPath[] =
  ['config', 'request.config', 'response.config'];

/** Properties on the "API message" portion of a captured error. */
export const CAPTURED_ERROR_API_MESSAGE_PROPS: (keyof typeof CAPTURED_ERROR_API_MESSAGE_BASE)[] =
  ['config', 'request', 'response'];

/** A captured error that hasn't been augmented yet. */
export const CAPTURED_ERROR_BASE: CapturedErrorBase = {
  applicationContext: {
    apiMessage: {},
    moduleName: ''
  },
  browsingContext: {
    url: '',
    userAgent: ''
  },
  errorContext: {},
  isCaptured: true,
  isHandled: false,
  message: '',
  name: '',
  severity: DEFAULT_ERROR_SEVERITY,
  subtype: DEFAULT_ERROR_SUBTYPE,
  type: DEFAULT_ERROR_TYPE,
  userContext: {
    displayLanguage: '',
    language: '',
    translatedMessage: ''
  }
};

/** Properties nested on a captured error. */
export const CAPTURED_ERROR_MESSAGE_PROPS: (keyof typeof CAPTURED_ERROR_BASE)[] =
  ['browsingContext', 'errorContext', 'userContext'];

// ─── Logging Constants ──────────────────────────────
// (consolidated from errorHandling.logging.constants)

export const ERROR_LOG_API_ENDPOINT = 'Configuration/LogMessage';
export const ERROR_LOG_COMPONENT_NAME_PREFIX = 'AscendonUI';
export const ERROR_LOG_RESTRICTED_MODULES = [
  `${ERROR_LOG_COMPONENT_NAME_PREFIX}.${
    allApplications[Application.Configuration].name
  }`,
  `${ERROR_LOG_COMPONENT_NAME_PREFIX}.${
    allApplications[Application.CustomerCare].name
  }`,
  `${ERROR_LOG_COMPONENT_NAME_PREFIX}.${
    allApplications[Application.Reporting].name
  }`,
  `${ERROR_LOG_COMPONENT_NAME_PREFIX}.${
    allApplications[Application.Security].name
  }`
];

export const ERROR_LOG_ITEM_MESSAGE_ATTRIBUTE_SEPARATOR = '\n--\n';
export const ERROR_LOG_ITEM_MESSAGE_SEPARATOR = '\n----\n';

/** Maximum length of properties in an error log item. */
export const ERROR_LOG_ITEM_MAX_LENGTHS = {
  ComponentName: 50,
  Message: 5000,
  MessageWithoutSeparator: 5000 - ERROR_LOG_ITEM_MESSAGE_SEPARATOR.length,
  OriginatingOperation: 100,
  SubComponentName: 50
};

/** Default message to display if sensitive information has been redacted (e.g. from HTTP headers). */
export const REDACTED_MESSAGE = '<Redacted>';

/** Map of API fault codes that contain sensitive information to their redacted message. */
export const REDACTED_FAULT_MESSAGES_MAP: Record<string, string> = {
  [SESSION_EXPIRED_FAULT_CODE]: REDACTED_MESSAGE
};

/** List of HTTP headers that contain sensitive information that should be redacted. */
export const REDACTED_HEADERS = [
  AUTHORIZATION_HEADER.toLowerCase(),
  SESSION_ID_HEADER.toLowerCase()
];
